/**
 * ClientUsage — Network-wide > Usage. "Who is using too much bandwidth?"
 *
 * THE HONESTY PROBLEM THIS SCREEN EXISTS TO AVOID: the AP reports rx_bytes/tx_bytes
 * CUMULATIVE SINCE ASSOCIATION. A table sorted by those totals looks like "heaviest users" and
 * actually shows "who has been connected longest" — confidently wrong, and impossible for an
 * operator to catch by eye. So the ranked column is the sustained average over each client's
 * OWN connected window, and the cumulative total is shown beside it as context, never as the
 * sort key. The header says so in words, because a number nobody can interpret is worse than
 * no number.
 *
 * Clients connected for less than the server's `min_connected_sec` are counted in the totals
 * but not ranked; they are surfaced as a footnote rather than hidden, so the numbers add up.
 *
 * Design: existing Meridian tokens/components only (PageHeader, md-card, Button, tokens
 * bg-surface/surface-2/border-line/text-ink/ink-muted/ink-subtle). The status ramp
 * (healthy/warn/critical) is reserved for operational state, so "over threshold" uses `warn`
 * — it IS an operational condition — while everything else stays neutral.
 */
import { useState } from "react";
import { useSession } from "@/stores/session";
import { useClientUsage } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { fmtBytes, fmtRate, fmtDuration } from "@/lib/format";
import type { ClientUsageRow } from "@/types/domain";

const INPUT =
  "w-24 rounded border border-line bg-surface px-2 py-1 text-ink outline-none focus:border-brand";

export default function ClientUsage() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const [thresholdMbps, setThresholdMbps] = useState<number | null>(null);
  const [draft, setDraft] = useState("");

  const { data, isLoading, isFetching, error, refetch } = useClientUsage(siteId, {
    top: 50,
    thresholdMbps,
  });

  const applyThreshold = (e: React.FormEvent) => {
    e.preventDefault();
    const v = parseFloat(draft);
    setThresholdMbps(Number.isFinite(v) && v > 0 ? v : null);
  };

  return (
    <div>
      <PageHeader
        title="Usage"
        subtitle={
          siteName
            ? `Sustained bandwidth per connected client in ${siteName}`
            : "Sustained bandwidth per connected client"
        }
        actions={
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Reading live client state…</div>}

      {data && (
        <>
          {/* What the ranked number MEANS. Without this an operator reads the average as a
              current speed and the total as a bill. */}
          <p className="mb-4 max-w-3xl text-sm text-ink-muted">
            Ranked by <span className="font-medium text-ink">average rate</span> — each client&rsquo;s
            traffic divided by how long it has been connected. Totals are cumulative since the
            client joined, so a long-connected device shows more data without being a heavy user.
          </p>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Stat label="Connected" value={String(data.clients)} />
            <Stat label="Upload" value={fmtBytes(data.totals.upload_bytes)} />
            <Stat label="Download" value={fmtBytes(data.totals.download_bytes)} />
            {thresholdMbps != null && (
              <Stat
                label={`Over ${thresholdMbps} Mbps`}
                value={String(data.over_threshold)}
                tone={data.over_threshold > 0 ? "warn" : undefined}
              />
            )}
          </div>

          <form onSubmit={applyThreshold} className="mb-4 flex items-center gap-2 text-sm">
            <label htmlFor="threshold" className="text-ink-muted">
              Flag above
            </label>
            <input
              id="threshold"
              className={INPUT}
              inputMode="decimal"
              placeholder="Mbps"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button variant="secondary" type="submit">Apply</Button>
            {thresholdMbps != null && (
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setThresholdMbps(null);
                  setDraft("");
                }}
              >
                Clear
              </Button>
            )}
          </form>

          {data.top.length === 0 ? (
            <EmptyState
              title="No clients to rank"
              hint={
                data.clients > 0
                  ? `All ${data.clients} connected client(s) have been associated for less than ${data.min_connected_sec}s, so an average would be meaningless.`
                  : "No clients are currently associated to an access point in this scope."
              }
            />
          ) : (
            <div className="md-card overflow-x-auto p-0">
              <table className="w-full text-left text-xs">
                <thead className="text-ink-subtle">
                  <tr className="border-b border-line">
                    <th className="px-3 py-2 font-normal">Client</th>
                    <th className="px-3 py-2 font-normal">SSID</th>
                    <th className="px-3 py-2 font-normal">Access point</th>
                    <th className="px-3 py-2 text-right font-normal">Connected</th>
                    <th className="px-3 py-2 text-right font-normal">Upload</th>
                    <th className="px-3 py-2 text-right font-normal">Download</th>
                    <th className="px-3 py-2 text-right font-normal">Average rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top.map((c) => (
                    <Row key={c.mac} c={c} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.ignored_too_brief > 0 && (
            /* Surfaced, not hidden: otherwise the row count does not match "Connected". */
            <p className="mt-3 text-xs text-ink-subtle">
              {data.ignored_too_brief} client(s) connected for less than {data.min_connected_sec}s
              are included in the totals but not ranked — too short a window for a meaningful
              average.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="md-card px-3 py-2">
      <div className="text-[0.625rem] uppercase tracking-wide text-ink-subtle">{label}</div>
      <div className={`text-lg font-semibold ${tone === "warn" ? "text-warn" : "text-ink"}`}>
        {value}
      </div>
    </div>
  );
}

function Row({ c }: { c: ClientUsageRow }) {
  return (
    <tr className={`border-b border-line/60 ${c.over_threshold ? "bg-warn/5" : ""}`}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{c.hostname || c.mac}</span>
          {c.over_threshold && (
            <span className="rounded bg-warn/15 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-warn">
              over
            </span>
          )}
        </div>
        {c.hostname && <div className="font-mono text-[0.6875rem] text-ink-subtle">{c.mac}</div>}
      </td>
      <td className="px-3 py-2 text-ink-muted">{c.ssid ?? "—"}</td>
      <td className="px-3 py-2 text-ink-muted">{c.device_name ?? c.device_serial ?? "—"}</td>
      <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
        {fmtDuration(c.connected_sec)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{fmtBytes(c.upload_bytes)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
        {fmtBytes(c.download_bytes)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-medium text-ink">{fmtRate(c.avg_bps)}</td>
    </tr>
  );
}
